// =============================================================================
// AR invoice detail — React port of the ar-due prototype's detail panel.
//
// ONE composed read (GET /invoice-dashboard/invoices/{n}/screen) paints the
// whole page: header + payment schedule + sub-invoices (each with its
// verification packet EMBEDDED) + audit events + live enrichment (parties /
// reps) + live cash receipts. Actions stay their own calls.
//
// THE architectural rule (spec): Upload / Validate / Send / Re-verify /
// Override are rendered FROM the packet's server-driven `actions` map
// ({enabled, label, reason}) — business rules live once, on the server, and
// disabled buttons show the server's reason verbatim. Downloads are
// navigation and stay client-owned. Sends/pays always confirm first.
// =============================================================================
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Badge, Button, Spinner, Table } from "react-bootstrap";
import { FaChevronLeft, FaFileInvoiceDollar, FaSync } from "react-icons/fa";
import { apiFetch, hasAction } from "../utils/auth";
import AttachmentsButton from "../components/ReferenceDocsPanel";
import { MATCH_BADGE, PAID_BADGE, POST_BADGE, SEND_BADGE, fmtDate, money } from "./arShared";
import "../pagecss/Webappcss.css";
import "../pagecss/QuoteWizard.css";

const API_URL = process.env.REACT_APP_API_URL;

function Card({ title, right, children }) {
  return (
    <div style={{ border: "1px solid #E2E8F0", borderRadius: 12, background: "#fff", marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", borderBottom: "1px solid #F1F5F9" }}>
        <div style={{ fontWeight: 700, fontSize: 14 }}>{title}</div>
        {right}
      </div>
      <div style={{ padding: 14 }}>{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 6, fontSize: 13 }}>
      <span style={{ color: "#94A3B8", marginRight: 6 }}>{label}:</span>
      <span style={{ color: "#0F172A" }}>{children || "—"}</span>
    </div>
  );
}

export default function InvoiceDetail() {
  const { invoiceNum } = useParams();
  const navigate = useNavigate();

  const [scr, setScr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);        // success toast line
  const [actErr, setActErr] = useState(null);
  const [busy, setBusy] = useState(null);        // "validate:123" etc.
  const [openSeq, setOpenSeq] = useState(null);
  const uploadRef = useRef(null);
  const uploadTarget = useRef(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await apiFetch(`${API_URL}/api/invoice-dashboard/invoices/${encodeURIComponent(invoiceNum)}/screen`);
      const d = await r.json().catch(() => null);
      if (!r.ok || d?.success === false) throw new Error(d?.error || `Invoice ${invoiceNum} not found (HTTP ${r.status}).`);
      setScr(d);
    } catch (e) { setError(e.message); setScr(null); }
    finally { setLoading(false); }
  }, [invoiceNum]);

  useEffect(() => { load(); }, [load]);

  const header = scr?.header || {};
  const subs = useMemo(() => (scr?.subInvoices || []).slice().sort((a, b) => a.seq - b.seq), [scr]);
  const sched = scr?.paymentSchedule || [];
  const enrich = scr?.enrichment?.header || null;
  const payments = scr?.payments || null;
  const isCash = header.planCategory === "CASH";

  // ── KPIs ───────────────────────────────────────────────────────────────────
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const dueNow = subs.filter((s) => s.dueDate && new Date(s.dueDate) <= today && s.paidStatus !== "Paid")
    .reduce((t, s) => t + Math.max(0, (s.amount || 0) - (s.paidAmount || 0)), 0);
  const next = subs.filter((s) => s.dueDate && new Date(s.dueDate) > today && s.paidStatus !== "Paid")
    .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))[0];
  const totalRemaining = header.invoiceBalance != null
    ? header.invoiceBalance
    : subs.reduce((t, s) => t + Math.max(0, (s.amount || 0) - (s.paidAmount || 0)), 0);
  const matched = subs.filter((s) => s.matchStatus === "Matched" || s.matchStatus === "NoCharge").length;

  // ── downloads (auth'd fetch → blob → open; SAS links open directly) ────────
  const openBlob = async (path, name) => {
    setActErr(null);
    try {
      const r = await apiFetch(`${API_URL}${path}`);
      if (!r.ok) {
        const j = await r.json().catch(() => null);
        throw new Error(j?.error || j?.message || `Download failed (HTTP ${r.status}) — is wkhtmltopdf deployed?`);
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { setActErr(e.message); }
  };

  const openPoFile = async (installmentId) => {
    setActErr(null);
    try {
      const r = await apiFetch(`${API_URL}/api/invoice-dashboard/installments/${installmentId}/po-file`);
      const d = await r.json().catch(() => null);
      if (d?.hasFile && d.downloadUrl) window.open(d.downloadUrl, "_blank", "noopener");
      else setNote(d?.message || "No uploaded PO file for this invoice.");
    } catch (e) { setActErr(e.message); }
  };

  // ── server-driven installment actions ─────────────────────────────────────
  const post = async (path, body, opts = {}) => {
    const r = await apiFetch(`${API_URL}${path}`, { method: "POST", ...(body !== undefined ? { body: JSON.stringify(body) } : {}), ...opts });
    const d = await r.json().catch(() => null);
    return { ok: r.ok, status: r.status, d };
  };

  const runAction = async (key, sub) => {
    const id = sub.installmentId;
    setActErr(null); setNote(null); setBusy(`${key}:${id}`);
    try {
      if (key === "reuploadPo") {
        uploadTarget.current = id;
        uploadRef.current?.click();
        return;
      }
      if (key === "overrideMatch") {
        const reason = window.prompt(
          "Override this line's match verdict.\nWhat did you check (order ack · customer PO · invoice) and why is the computed verdict wrong? (min 5 chars)");
        if (!reason || reason.trim().length < 5) return;
        const { ok, d } = await post(`/api/invoice-dashboard/installments/${id}/override`, { reason });
        if (!ok) throw new Error(d?.error || "Override failed.");
        setNote(d?.message || "Override recorded.");
      } else if (key === "reverify") {
        const { ok, d } = await post(`/api/invoice-dashboard/installments/${id}/reverify`);
        if (!ok) throw new Error(d?.error || "Re-verify failed.");
        setNote(d?.message || "Re-verified.");
      } else if (key === "validate") {
        const { ok, d } = await post(`/api/invoice-dashboard/installments/${id}/validate`, {});
        if (!ok) throw new Error(d?.error || "Validate failed.");
        setNote(d?.message || "Validated.");
      } else if (key === "send") {
        if (!window.confirm(`Send ${sub.customerInvoiceNumber} to the customer now?`)) return;
        let { ok, status, d } = await post(`/api/invoice-dashboard/installments/${id}/send`);
        if (!ok && status === 400 && (d?.error || "").toLowerCase().includes("payment")
          && window.confirm(`${d.error}\n\nSend anyway (force past the payment gate)?`)) {
          ({ ok, d } = await post(`/api/invoice-dashboard/installments/${id}/send?force=true`));
        }
        if (!ok) throw new Error(d?.error || "Send failed.");
        setNote(d?.message || "Sent.");
      } else if (key === "pay") {
        const amt = window.prompt(`Record a payment for ${sub.customerInvoiceNumber}.\nAmount (blank = full installment ${money(sub.amount)}):`);
        if (amt === null) return;
        if (!window.confirm(`Record ${amt ? money(Number(amt)) : "the full installment"} as paid in Epicor?`)) return;
        const { ok, d } = await post(`/api/invoice-dashboard/installments/${id}/pay`, amt ? { amount: Number(amt) } : {});
        if (!ok) throw new Error(d?.error || d?.message || "Payment failed.");
        setNote(d?.message || "Payment recorded.");
      }
      await load();
    } catch (e) { setActErr(e.message); }
    finally { setBusy(null); }
  };

  const handleUploadFile = async (file) => {
    const id = uploadTarget.current; uploadTarget.current = null;
    if (!file || !id) return;
    setBusy(`upload:${id}`); setActErr(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // multipart — bypass the JSON helper, keep the bearer token.
      const token = sessionStorage.getItem?.("token");
      const r = await fetch(`${API_URL}/api/invoice-dashboard/installments/${id}/po`, {
        method: "POST", body: fd,
        headers: { Authorization: `Bearer ${window.sessionStorage.getItem("token") || token || ""}` },
      });
      const d = await r.json().catch(() => null);
      if (!r.ok || d?.success === false || d?.accepted === false)
        throw new Error(d?.message || d?.error || `Upload rejected (HTTP ${r.status}).`);
      setNote(d?.message || "PO uploaded and matched.");
      await load();
    } catch (e) { setActErr(e.message); }
    finally { setBusy(null); }
  };

  // ── resend (plan-aware, REAL email — hard confirm) ─────────────────────────
  const resend = async () => {
    const to = window.prompt("Resend invoice documents.\nTO email:", enrich?.contactEmailAddr || "");
    if (!to) return;
    const ccRaw = window.prompt("CC (comma-separated, blank for none):", "") ?? "";
    const cc = ccRaw.split(",").map((s) => s.trim()).filter(Boolean);
    const docDesc = isCash ? "the invoice document" : `${subs.length} invoice documents (bundled in one email)`;
    if (!window.confirm(`This sends a REAL email now with ${docDesc} to:\n${to}${cc.length ? `\nCC: ${cc.join(", ")}` : ""}\n\nProceed?`)) return;
    setBusy("resend"); setActErr(null);
    try {
      const { ok, d } = await post(`/api/invoice-dashboard/invoices/${header.invoiceNum}/send-documents`, { toEmail: to, cc });
      if (!ok) throw new Error(d?.error || d?.message || "Resend failed.");
      setNote(d?.message || d?.summary || "Documents sent.");
    } catch (e) { setActErr(e.message); }
    finally { setBusy(null); }
  };

  // ── CTO monthly panel ──────────────────────────────────────────────────────
  const ctoState = (s) => {
    if (s.paidStatus === "Paid") return { key: "paid", label: "paid", color: "#16A34A" };
    if (s.sendStatus === "Sent") return { key: "billed", label: "billed", color: "#2563EB" };
    if (s.matchStatus === "Short" || s.matchStatus === "Over" || s.matchStatus === "CapBreach" || s.matchStatus === "DuplicatePO")
      return { key: "problem", label: "fix", color: "#DC2626" };
    if (s.poStatus === "Validated" && s.matchStatus === "Matched") return { key: "ready", label: "ready", color: "#059669" };
    if (s.customerPONumber && s.matchStatus === "Matched") return { key: "validate", label: "validate", color: "#7C3AED" };
    if (s.customerPONumber) return { key: "poin", label: "PO in", color: "#B45309" };
    return { key: "awaiting", label: "awaiting", color: "#94A3B8" };
  };

  function CtoPanel() {
    const states = subs.map((s) => ({ s, st: ctoState(s) }));
    const paidAmt = subs.reduce((t, s) => t + (s.paidStatus === "Paid" ? s.amount : s.paidAmount || 0), 0);
    const total = subs.reduce((t, s) => t + (s.amount || 0), 0);
    const cnt = (k) => states.filter((x) => x.st.key === k).length;
    const nextAction =
      states.find((x) => x.st.key === "problem") ? `Fix month ${states.find((x) => x.st.key === "problem").s.seq} — flagged match.` :
      states.find((x) => x.st.key === "validate") ? `Validate month ${states.find((x) => x.st.key === "validate").s.seq}'s PO.` :
      states.find((x) => x.st.key === "ready") ? `Month ${states.find((x) => x.st.key === "ready").s.seq} is cleared — send when due.` :
      states.find((x) => x.st.key === "awaiting") ? `Awaiting the customer PO for month ${states.find((x) => x.st.key === "awaiting").s.seq}.` :
      `✓ All ${subs.length} month(s) billed.`;
    return (
      <Card title="🔄 CTO monthly billing">
        <div style={{ fontSize: 13, color: "#64748B", marginBottom: 8 }}>
          {subs.length} monthly payment(s) · {cnt("billed") + cnt("paid")} billed · {cnt("paid")} paid · {cnt("awaiting")} awaiting PO · {cnt("ready")} ready to bill · {cnt("problem")} to fix
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
          {states.map(({ s, st }) => (
            <span key={s.seq} title={`Month ${s.seq}: ${st.label} · ${money(s.amount)} · ${fmtDate(s.dueDate)}${s.customerPONumber ? ` · PO ${s.customerPONumber}` : ""}`}
              onClick={() => setOpenSeq(s.seq)}
              style={{ cursor: "pointer", padding: "4px 9px", borderRadius: 8, fontSize: 11.5, fontWeight: 700, color: "#fff", background: st.color }}>
              {s.seq} · {st.label}
            </span>
          ))}
        </div>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>{nextAction}</div>
        <div style={{ position: "relative", height: 8, borderRadius: 5, background: "#E2E8F0" }}>
          <div style={{ position: "absolute", inset: 0, width: `${total > 0 ? Math.min(100, (paidAmt / total) * 100) : 0}%`, background: "#16A34A", borderRadius: 5 }} />
          <div title="Customer becomes return-eligible around 50% paid"
            style={{ position: "absolute", left: "50%", top: -3, width: 3, height: 14, background: "#7C3AED", borderRadius: 2 }} />
        </div>
        <div style={{ fontSize: 11.5, color: "#94A3B8", marginTop: 4 }}>
          {money(paidAmt)} of {money(total)} paid · purple mark = ~50% return-eligibility point
        </div>
      </Card>
    );
  }

  // ── sub-invoice drill (packet + actions) ──────────────────────────────────
  function SubDrill({ s }) {
    const pk = s.packet;
    const actions = pk?.actions || {};
    const gate = { reuploadPo: "00069", validate: "00070", reverify: "00070", send: "00071", overrideMatch: "00073" };
    const actionOrder = ["reuploadPo", "validate", "send", "reverify", "overrideMatch"];
    return (
      <div style={{ background: "#F8FAFC", padding: 14, borderTop: "1px solid #E2E8F0" }}>
        {pk?.issue && (
          <div style={{ borderLeft: "4px solid #DC2626", background: "#FEF2F2", padding: "8px 12px", borderRadius: 6, marginBottom: 10, fontSize: 13 }}>
            <b>{pk.issue.title}</b> — {pk.issue.message}
          </div>
        )}
        {pk?.overriddenBy && (
          <div style={{ borderLeft: "4px solid #7C3AED", background: "#F5F3FF", padding: "8px 12px", borderRadius: 6, marginBottom: 10, fontSize: 13 }}>
            ⚑ Overridden by {pk.overriddenBy} — was {pk.overrodeStatus}. Reason: {pk.overrideReason}
          </div>
        )}
        {s.matchDetail && <div style={{ fontSize: 12.5, color: "#64748B", marginBottom: 10 }}><b>Why this match:</b> {s.matchDetail}</div>}

        {/* Verification packet — the 3-way match as documents */}
        {pk && (
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", marginBottom: 6 }}>📁 Verification packet</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", fontSize: 12.5, alignItems: "center" }}>
              <Button size="sm" variant="outline-secondary" disabled={!pk.orderAck?.available}
                title={pk.orderAck?.available ? undefined : "no per-PO ack found"}
                onClick={() => openBlob(`/api/invoice-dashboard/installments/${s.installmentId}/order-ack`, "ack.pdf")}>
                📄 {pk.orderAck?.label || "Order Ack"}{pk.orderAck?.amount != null ? ` · ${money(pk.orderAck.amount)}` : ""}
              </Button>
              <Button size="sm" variant="outline-secondary" disabled={!pk.customerPo?.available}
                title={pk.customerPo?.available ? undefined : "no uploaded PO file"}
                onClick={() => openPoFile(s.installmentId)}>
                🧾 {pk.customerPo?.label || "Customer PO"}{pk.customerPo?.amount != null ? ` · ${money(pk.customerPo.amount)}` : ""}
              </Button>
              <Button size="sm" variant="outline-secondary"
                onClick={() => openBlob(`/api/invoices/installments/${s.installmentId}/pdf`, "invoice.pdf")}>
                💵 {pk.invoiceDoc?.label || `AR Invoice ${s.customerInvoiceNumber}`} · {money(pk.invoiceDoc?.amount ?? s.amount)}
              </Button>
              {pk.perInvoiceCap > 0 && (
                <span style={{ fontWeight: 600, color: (pk.capHeadroom ?? 0) < 0 ? "#DC2626" : "#16A34A" }}>
                  {(pk.capHeadroom ?? 0) < 0
                    ? `${money(-(pk.capHeadroom))} OVER the ${money(pk.perInvoiceCap)} AP cap`
                    : `${money(pk.capHeadroom)} clear of the ${money(pk.perInvoiceCap)} AP cap`}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Server-driven actions — rendered from the packet's enabled/reason map */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {actionOrder.map((key) => {
            const a = actions[key];
            if (!a || !hasAction(gate[key], true)) return null;
            const running = busy === `${key}:${s.installmentId}` || busy === `upload:${s.installmentId}`;
            return (
              <Button key={key} size="sm"
                variant={a.primary ? "primary" : key === "send" ? "success" : "outline-secondary"}
                disabled={!a.enabled || running}
                title={a.reason || undefined}
                style={{ opacity: a.enabled ? 1 : 0.45 }}
                onClick={() => runAction(key, s)}>
                {running ? <Spinner animation="border" size="sm" /> : a.label}
              </Button>
            );
          })}
          {s.sendStatus === "Sent" && s.paidStatus !== "Paid" && hasAction("00072", true) && (
            <Button size="sm" variant="outline-success" disabled={busy === `pay:${s.installmentId}`}
              onClick={() => runAction("pay", s)}>
              💵 Pay…
            </Button>
          )}
          {s.validatedBy && (
            <span style={{ fontSize: 12, color: "#94A3B8" }}>Validated by {s.validatedBy} · {fmtDate(s.validatedAt)}</span>
          )}
        </div>
      </div>
    );
  }

  const subRowTone = (s) => {
    if (s.paidStatus === "Paid") return "#F0FDF4";
    if (s.sendStatus === "Sent") return "#EFF6FF";
    if (s.dueDate && new Date(s.dueDate) < today) return "#FEF2F2";
    return "#fff";
  };

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <div className="qw-root content-halo" style={{ padding: "28px 24px 36px", maxWidth: "min(1180px, 96vw)", margin: "84px auto 56px", borderRadius: 16, minHeight: "calc(100vh - 140px)" }}>
      <div className="qw-header" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div className="qw-header-icon"><FaFileInvoiceDollar size={18} /></div>
          <div>
            <div className="qw-header-title">Invoice #{invoiceNum}</div>
            <div className="qw-header-sub">
              {header.customerName || "…"}{header.planCategory ? ` · ${header.planCategory}${header.termsCode ? ` (${header.termsCode})` : ""}` : ""}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <Button className="qw-btn qw-btn-sm" style={{ background: "rgba(255,255,255,.18)", color: "#fff", border: "none", fontWeight: 600 }} onClick={load}><FaSync /></Button>
          <AttachmentsButton entityType="Invoice" entityId={invoiceNum} />
          <Button className="qw-btn qw-btn-sm" style={{ background: "rgba(255,255,255,.18)", color: "#fff" }} onClick={() => navigate(-1)}>
            <FaChevronLeft style={{ marginRight: 6 }} /> Back
          </Button>
        </div>
      </div>

      {/* hidden PO re-upload picker (must be in the DOM) */}
      <input ref={uploadRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" style={{ display: "none" }}
        onChange={(e) => { handleUploadFile(e.target.files?.[0]); e.target.value = ""; }} />

      {note && <Alert variant="success" dismissible onClose={() => setNote(null)} style={{ fontSize: 13 }}>{note}</Alert>}
      {actErr && <Alert variant="danger" dismissible onClose={() => setActErr(null)} style={{ fontSize: 13 }}>{actErr}</Alert>}

      {loading ? (
        <div style={{ textAlign: "center", padding: 48, color: "#64748B" }}>
          <Spinner animation="border" size="sm" style={{ marginRight: 8 }} /> Loading invoice… (reads live balances from Epicor)
        </div>
      ) : error ? (
        <Alert variant="danger" style={{ fontSize: 13 }}>{error}</Alert>
      ) : scr && (
        <>
          {/* KPI strip */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
            {[
              ["Due now", money(dueNow), dueNow > 0 ? "#DC2626" : "#16A34A"],
              ["Next payment", next ? `${money(next.amount)} · ${fmtDate(next.dueDate)}` : "—"],
              ["Total remaining", money(totalRemaining)],
              ["Invoice total", money(header.invoiceAmount)],
            ].map(([l, v, tone]) => (
              <div key={l} style={{ minWidth: 140, padding: "10px 14px", border: "1px solid #E2E8F0", borderRadius: 10, background: "#fff" }}>
                <div style={{ fontSize: 11, color: "#94A3B8", textTransform: "uppercase" }}>{l}</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: tone || "#0F172A" }}>{v}</div>
              </div>
            ))}
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              {header.paidStatus && <Badge bg={header.paidStatus === "full" ? "success" : header.paidStatus === "partial" ? "warning" : "secondary"} text={header.paidStatus === "partial" ? "dark" : undefined}>paid: {header.paidStatus}</Badge>}
              {subs.length > 0 && <Badge bg={matched === subs.length ? "success" : "secondary"}>{matched} of {subs.length} matched</Badge>}
              <Badge bg={SEND_BADGE[header.sendStatus] || "secondary"}>{header.sendStatus}</Badge>
              {header.posted && <Badge bg="dark">Posted in Epicor</Badge>}
              {header.isHeld && <Badge bg="warning" text="dark" title={header.holdReason || undefined}>🔒 held</Badge>}
            </div>
          </div>

          {header.planCategory === "CTO" && subs.length > 0 && <CtoPanel />}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <Card title="👤 Customer & Sales Rep">
              <Field label="Sold-To">{enrich?.customerName || header.customerName} {enrich?.customerCustID ? `(${enrich.customerCustID})` : ""}</Field>
              <Field label="Bill-To (AP)">{enrich?.customerBTName || enrich?.btCustID}</Field>
              <Field label="AP contact">{enrich?.contactName}</Field>
              <Field label="AP email">{enrich?.contactEmailAddr}</Field>
              <Field label="AP phone">{enrich?.contactPhoneNum}</Field>
              <Field label="Customer PO">{header.customerPONum}</Field>
              <Field label="Sales rep">
                {[1, 2, 3, 4, 5].map((i) => enrich?.[`salesRepName${i}`] || enrich?.[`salesRepCode${i}`]).filter(Boolean).join(" · ") || "—"}
              </Field>
              <Field label="Sales order">{header.orderNum > 0 ? header.orderNum : "—"}</Field>
              <Field label="Invoice date">{fmtDate(header.invoiceDate)}</Field>
              <Field label="Review decision">{header.reviewDecision}</Field>
              {scr.enrichment?.status !== "ok" && (
                <div style={{ fontSize: 12, color: "#B45309" }}>⚠ Live Epicor enrichment unavailable — showing local data.</div>
              )}
            </Card>

            <Card title="📁 Documents" right={
              hasAction("00071", true) && (
                <Button size="sm" variant="outline-primary" disabled={busy === "resend"} onClick={resend}>
                  ✉ Resend invoice…
                </Button>
              )
            }>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <Button variant="outline-secondary" size="sm" disabled={!header.orderNum}
                  title={header.orderNum ? undefined : "no linked order"}
                  onClick={() => openBlob(`/api/reports/order/${header.orderNum}?format=pdf&download=true`)}>
                  📄 Sales Order Ack #{header.orderNum || "—"}
                </Button>
                <Button variant="outline-secondary" size="sm"
                  onClick={() => {
                    const withFile = subs.find((s) => s.hasPoFile);
                    if (withFile) openPoFile(withFile.installmentId);
                    else setNote("No uploaded PO file for this invoice.");
                  }}>
                  🧾 Customer PO{header.customerPONum ? ` · ${header.customerPONum}` : ""}
                </Button>
                <Button variant="outline-secondary" size="sm"
                  onClick={() => openBlob(`/api/reports/invoice/${header.invoiceNum}?format=pdf&download=true`)}>
                  💵 AR Invoice #{header.invoiceNum}
                </Button>
              </div>
            </Card>
          </div>

          {/* Payment schedule (Epicor InvcSched) */}
          <Card title="📆 Payment schedule">
            {sched.length === 0 ? (
              <div style={{ fontSize: 13, color: "#64748B" }}>Single payment (N30).</div>
            ) : (
              <Table size="sm" style={{ fontSize: 13, marginBottom: 0 }}>
                <thead><tr><th>#</th><th>Days</th><th>Due</th><th>%</th><th style={{ textAlign: "right" }}>Amount</th></tr></thead>
                <tbody>
                  {sched.map((s) => (
                    <tr key={s.paySeq}>
                      <td>{s.paySeq}</td><td>{s.payDays}</td><td>{fmtDate(s.payDueDate ?? s.dueDate)}</td>
                      <td>{s.payPercent != null ? Number(s.payPercent).toFixed(4) : "—"}</td>
                      <td style={{ textAlign: "right" }}>{money(s.docPayAmount ?? s.payAmount)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>

          {/* Sub-invoices */}
          {subs.length > 0 && (
            <Card title={`🧾 Sub-invoices (${subs.length})`}>
              <div style={{ fontSize: 12, color: "#64748B", marginBottom: 8 }}>
                Each payment-schedule line is presented to the customer as its own invoice
                {" "}<b>{header.invoiceNum}-Seq</b> with its own PO — this is what the hospital AP matches and pays.
                {isCash && " (N30's PO is validated at convert — no per-installment upload.)"}
              </div>
              <div style={{ border: "1px solid #E2E8F0", borderRadius: 10, overflow: "hidden" }}>
                <div style={{ display: "grid", gridTemplateColumns: "44px 120px 1fr 110px 110px 100px 110px 110px 28px", gap: 8, padding: "8px 12px", background: "#F8FAFC", fontSize: 11, fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>
                  <div>Seq</div><div>Sub-invoice</div><div>Your PO #</div><div style={{ textAlign: "right" }}>Amount</div><div>Due</div><div>Match</div><div>Cleared</div><div>Paid</div><div />
                </div>
                {subs.map((s) => (
                  <div key={s.installmentId}>
                    <div onClick={() => setOpenSeq(openSeq === s.seq ? null : s.seq)}
                      style={{ display: "grid", gridTemplateColumns: "44px 120px 1fr 110px 110px 100px 110px 110px 28px", gap: 8, padding: "9px 12px", borderTop: "1px solid #F1F5F9", fontSize: 13, cursor: "pointer", background: subRowTone(s), alignItems: "center" }}>
                      <div>{s.seq}</div>
                      <div style={{ fontWeight: 600 }}>{s.customerInvoiceNumber}</div>
                      <div style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{s.customerPONumber || <span style={{ color: "#94A3B8" }}>(none)</span>}</div>
                      <div style={{ textAlign: "right" }}>
                        {s.paidAmount > 0.004 && s.paidStatus !== "Paid"
                          ? <><span style={{ textDecoration: "line-through", color: "#94A3B8", marginRight: 4 }}>{money(s.amount)}</span>{money(s.amount - s.paidAmount)}</>
                          : s.paidStatus === "Paid" ? <span style={{ color: "#16A34A" }}>{money(s.amount)} paid</span> : money(s.amount)}
                      </div>
                      <div>{fmtDate(s.dueDate)}</div>
                      <div><Badge bg={MATCH_BADGE[s.matchStatus] || "secondary"}>{s.matchStatus}</Badge></div>
                      <div>
                        {isCash ? <span style={{ color: "#94A3B8", fontSize: 12 }}>n/a</span> :
                          <Badge bg={POST_BADGE[s.poStatus] || "secondary"}
                            title={s.poStatus === "Validated" ? "Clean 3-way match — cleared to send" : undefined}>
                            {s.sendStatus === "Sent" ? "Sent" : s.poStatus === "Validated" ? "✓ Cleared" : s.poStatus}
                          </Badge>}
                      </div>
                      <div><Badge bg={PAID_BADGE[s.paidStatus] || "secondary"}>{s.paidStatus}</Badge></div>
                      <div style={{ color: "#94A3B8" }}>{openSeq === s.seq ? "▾" : "▸"}</div>
                    </div>
                    {openSeq === s.seq && <SubDrill s={s} />}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Payments received */}
          <Card title="💵 Payments received">
            {(() => {
              const applied = header.invoiceAmount != null && header.invoiceBalance != null
                ? header.invoiceAmount - header.invoiceBalance : payments?.totalApplied;
              return (
                <>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
                    <Field label="Received & applied">
                      <b style={{ color: "#16A34A" }}>{money(applied)}</b>
                      {payments?.totalPending > 0 && <span style={{ color: "#B45309" }}> · {money(payments.totalPending)} pending</span>}
                    </Field>
                    <Field label="Balance remaining"><b>{money(header.invoiceBalance)}</b></Field>
                    <Field label="Installments">{subs.filter((s) => s.paidStatus === "Paid").length} paid · {subs.filter((s) => s.paidStatus === "PartiallyPaid").length} partial of {subs.length}</Field>
                  </div>
                  {payments?.success === false ? (
                    <Alert variant="warning" style={{ fontSize: 12.5 }}>Couldn't load live cash receipts: {payments.error}</Alert>
                  ) : (payments?.receipts || []).length === 0 ? (
                    <div style={{ fontSize: 13, color: "#64748B" }}>No cash receipts recorded against this invoice yet.</div>
                  ) : (
                    <Table size="sm" style={{ fontSize: 12.5, marginBottom: 0 }}>
                      <thead><tr><th>Check/ref</th><th>Deposit date</th><th style={{ textAlign: "right" }}>Applied</th><th>Applied to</th><th>Bank</th><th>Status</th><th style={{ textAlign: "right" }}>Bal. after</th></tr></thead>
                      <tbody>
                        {payments.receipts.map((p, i) => (
                          <tr key={i} style={{ opacity: p.reversed ? 0.5 : 1 }}>
                            <td>{p.checkRef || p.reference || p.headNum}</td>
                            <td>{fmtDate(p.tranDate)}</td>
                            <td style={{ textAlign: "right" }}>{money(p.amount)}</td>
                            <td>{p.invoiceRef > 0 ? `installment ${p.invoiceRef}` : "whole invoice"}</td>
                            <td>{p.bankAcctID || "—"}</td>
                            <td><Badge bg={p.reversed ? "danger" : p.posted ? "success" : "warning"} text={!p.reversed && !p.posted ? "dark" : undefined}>{p.reversed ? "reversed" : p.posted ? "posted" : "unposted"}</Badge></td>
                            <td style={{ textAlign: "right" }}>{money(p.balanceAfter)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                  <div style={{ fontSize: 11.5, color: "#94A3B8", marginTop: 6 }}>
                    "Received &amp; applied" is derived from the Epicor balance (face − balance), not the receipt rows —
                    unposted receipts and adjustments would skew a sum. Per-installment attribution waterfalls oldest first.
                  </div>
                </>
              );
            })()}
          </Card>

          {/* Audit history */}
          {(scr.events || []).length > 0 && (
            <Card title="📜 History">
              <div style={{ maxHeight: 220, overflowY: "auto" }}>
                {scr.events.slice(0, 20).map((e) => (
                  <div key={e.id} style={{ fontSize: 12.5, padding: "5px 0", borderBottom: "1px solid #F8FAFC" }}>
                    <b>{e.eventType}</b> · {e.actor} · {fmtDate(e.createdAt)}
                    {e.detail && <span style={{ color: "#64748B" }}> — {e.detail}</span>}
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
